import { AxiosResponse } from "axios";
import API from "serviceshift-ui/api-client";
import { trackFlows } from "@/lib/rumFlow";
import { updateQueryParam } from "@/lib/url";

import { withCloseoutOverride } from "@/composables/useCloseoutOverride";
import { AppointmentV2 } from "@/typings/appointment";
import { EventAPIResponse, ResourceAPIResponse } from "@/typings/scheduler";
import { initialState } from "../store";

const actions = {
  async fetchAppointmentDetails(
    _,
    data: { id: string | number }
  ): Promise<AppointmentV2> {
    const res = await API.makeRequest(`/v2/appointments/${data.id}`);
    return res.data as AppointmentV2;
  },
  async fetchTimelineEvents(
    { commit },
    filters: { start_date: string; end_date: string }
  ): Promise<void> {
    if (!filters.start_date) return;
    const res = (await API.makeRequest("/admin/v4/scheduler_events", {
      method: "GET",
      params: { filter: filters }
    })) as AxiosResponse<any, EventAPIResponse[]>;

    if (res.data) {
      await commit("update", {
        state: "timeline",
        data: {
          events: res.data.data,
          start_date: filters.start_date,
          events_fetched_at: new Date().toISOString()
        }
      });
    }
  },
  async fetchTimelineResources({ commit }): Promise<void> {
    const res = (await API.makeRequest("/admin/v4/scheduler_resources", {
      method: "GET"
    })) as AxiosResponse<any, ResourceAPIResponse[]>;
    if (res.data) {
      commit("update", {
        state: "timeline",
        data: { resources: res.data.data }
      });
    }
  },
  async updateTimelineFilter(
    { commit, state },
    data: { key: string; value: string[] | number[] }
  ) {
    commit("update", {
      state: "timeline",
      data: {
        filters: {
          ...state.timeline.filters,
          [data.key]: data.value
        }
      }
    });
    updateQueryParam(data.key, data.value.toString());
  },
  async setTimelineFilters(
    { commit, state },
    filters: {
      jobStatusIds?: string[];
      statuses?: string[];
      departmentIds?: number[];
      serviceCategoryIds?: number[];
    }
  ) {
    commit("update", {
      state: "timeline",
      data: {
        filters: { ...state.timeline.filters, ...filters }
      }
    });
  },
  async clearTimelineFilters({ commit }) {
    commit("update", {
      state: "timeline",
      data: {
        filters: { ...initialState().timeline.filters }
      }
    });
    Object.keys(initialState().timeline.filters).forEach((key) => {
      updateQueryParam(key, null);
    });
  },
  async assignAppointmentEvent(
    _,
    data: {
      appointmentId: string | number;
      technician_profile_id?: number | null;
      technician_profile_ids?: number[];
      pinned_reason?: string;
      skip_notifications?: boolean;
    }
  ) {
    const {
      appointmentId,
      technician_profile_id,
      technician_profile_ids,
      pinned_reason,
      skip_notifications
    } = data;

    const techPayload = technician_profile_ids
      ? { technician_profile_ids }
      : { technician_profile_id };

    // Dropping the last non-departed tech can complete the appointment, which the
    // server gates on close-out. withCloseoutOverride surfaces that 409 as the
    // shared override modal and retries with the reason, so every surface that
    // dispatches this action (drawer, unassign, board, timeline) inherits it.
    await withCloseoutOverride((overrideReason) =>
      API.makeRequest(`/appointments_calendar/${appointmentId}/set_tech`, {
        method: "PUT",
        headers: { ignoreError: true },
        data: {
          ...techPayload,
          pinned_reason,
          skip_notifications,
          ...(overrideReason
            ? { custom_form_override_reason: overrideReason }
            : {})
        }
      })
    );
  },
  async updateAppointmentEventDuration(
    _,
    data: {
      appointmentId: string | number;
      duration_minutes: number;
      skip_notifications?: boolean;
    }
  ) {
    const { appointmentId, duration_minutes, skip_notifications } = data;
    await API.makeRequest(`/v2/appointments/${appointmentId}`, {
      method: "PUT",
      data: {
        duration_minutes,
        skip_notifications
      }
    });
  },
  async rescheduleAppointmentEvent(
    _,
    data: {
      appointmentId: string | number;
      arrival_window_start: string;
      arrival_window_end: string;
      scheduled_time: string;
      skip_notifications?: boolean;
    }
  ) {
    const {
      appointmentId,
      arrival_window_start,
      arrival_window_end,
      scheduled_time,
      skip_notifications
    } = data;
    await API.makeRequest(`/v2/appointments/${appointmentId}`, {
      method: "PUT",
      data: {
        arrival_window_start,
        arrival_window_end,
        scheduled_time,
        skip_notifications
      }
    });
  },
  async updateAppointmentServiceCategory(
    _,
    data: {
      appointmentId: string | number;
      serviceCategoryId: string | number;
      skip_notifications?: boolean;
    }
  ) {
    const { appointmentId, serviceCategoryId, skip_notifications } = data;
    await API.makeRequest(`/v2/appointments/${appointmentId}`, {
      method: "PUT",
      data: {
        service_category_id: serviceCategoryId,
        skip_notifications
      }
    });
  },
  async updateAppointmentEvent(
    _,
    data: {
      appointmentId: string | number;
      technician_profile_id?: number | null;
      pinned_reason?: string;
      duration_minutes?: number;
      arrival_window_start?: string;
      arrival_window_end?: string;
      scheduled_time?: string;
      skip_notifications?: boolean;
      sync_technician_times?: boolean;
    }
  ) {
    const { appointmentId, ...rest } = data;
    await API.makeRequest(`/v2/appointments/${appointmentId}`, {
      method: "PUT",
      data: {
        ...rest
      }
    });
  }
};

// The three saves that move a job in time or between technicians, wrapped so
// every surface that dispatches them (board drops, drawer, dialogs) reports the
// reschedule_job and reassign_job flows from the one place the outcome is known.
const FLOW_ACTIONS = [
  "assignAppointmentEvent",
  "rescheduleAppointmentEvent",
  "updateAppointmentEvent"
] as const;

FLOW_ACTIONS.forEach((name) => {
  const action = actions[name] as (
    context: unknown,
    data: any
  ) => Promise<void>;
  actions[name] = ((context: unknown, data: any) =>
    trackFlows(data, () => action(context, data))) as any;
});

export default actions;
